import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, Popconfirm, Progress, message, Tag, Card, Row, Col, Statistic, Steps } from 'antd';
import { TeamOutlined, UserOutlined, CheckCircleOutlined, ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getSalesTeams, getSalespersons, createSalesperson } from '../../../services/api';
import { getSalesTasks, createSalesTask, updateSalesTask, deleteSalesTask, batchCreateTasks } from '../../../services/api';
import { getCurrentTaskYear, getTaskTeamMonths, batchSaveTaskTeamMonths } from '../../../services/api';

const quarterMonths = { 1: ['01', '02', '03'], 2: ['04', '05', '06'], 3: ['07', '08', '09'], 4: ['10', '11', '12'] };

export default function TaskDistribution() {
  // Step selection
  const [step, setStep] = useState('team'); // 'team' | 'person'
  const [yearRecord, setYearRecord] = useState(null);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((dayjs().month() + 1) / 3));
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM-01'));
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Data
  const [teams, setTeams] = useState([]);
  const [teamMonthData, setTeamMonthData] = useState([]);
  const [quarterAllocatedTotal, setQuarterAllocatedTotal] = useState(0);
  const [personData, setPersonData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Person modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm] = Form.useForm();

  // New person quick-add
  const [newPersonOpen, setNewPersonOpen] = useState(false);
  const [newPersonForm] = Form.useForm();

  const currentYear = dayjs().year();
  const years = [];
  for (let y = currentYear - 1; y <= currentYear + 2; y++) years.push(y);

  // Months for selected quarter
  const monthsInQuarter = quarterMonths[selectedQuarter] || [];
  const monthOptions = monthsInQuarter.map(m => ({
    label: `${selectedYear}年${parseInt(m)}月`,
    value: `${selectedYear}-${m}-01`,
  }));

  const fetchYear = async () => {
    try {
      const res = await getCurrentTaskYear(selectedYear);
      setYearRecord(res.data);
    } catch (err) { /* ignore */ }
  };

  const fetchTeamMonths = async () => {
    if (!yearRecord) return;
    setLoading(true);
    try {
      const q = yearRecord.quarters?.find(q => q.quarter === selectedQuarter);
      if (!q) { setTeamMonthData([]); return; }
      const [tmRes, tRes] = await Promise.all([
        getTaskTeamMonths({ yearId: yearRecord.id, quarterId: q.id, month: selectedMonth }),
        getSalesTeams(),
      ]);
      const existData = tmRes.data || [];
      const allTeams = tRes.data?.list || [];
      // 确保每个团队都在表格中（已有数据的合并进来）
      const existTeamIds = new Set(existData.map(d => d.teamId));
      const merged = [
        ...existData,
        ...allTeams.filter(t => !existTeamIds.has(t.id)).map(t => ({
          teamId: t.id, team: t, teamTargetAmount: 0,
        })),
      ];
      setTeamMonthData(merged);
      setTeams(allTeams);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };

  const fetchPersonData = async () => {
    if (!selectedTeam || !selectedMonth) return;
    setLoading(true);
    try {
      const [taskRes, pRes] = await Promise.all([
        getSalesTasks({ month: selectedMonth, teamId: selectedTeam }),
        getSalespersons({ teamId: selectedTeam }),
      ]);
      const existingIds = (taskRes.data || []).map(t => t.salespersonId);
      const stubs = (pRes.data?.list || [])
        .filter(p => !existingIds.includes(p.id))
        .map(p => ({
          salespersonId: p.id,
          salesperson: { id: p.id, name: p.name, teamId: p.teamId },
          targetAmount: 0,
          completed: 0,
          rate: 0,
        }));
      setPersonData([...(taskRes.data || []), ...stubs]);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { fetchYear(); }, [selectedYear]);
  // 切换季度时自动选中该季度第一个月
  useEffect(() => {
    const firstMonth = quarterMonths[selectedQuarter]?.[0];
    if (firstMonth) setSelectedMonth(`${selectedYear}-${firstMonth}-01`);
  }, [selectedQuarter, selectedYear]);
  useEffect(() => { if (step === 'team') fetchTeamMonths(); }, [yearRecord, selectedQuarter, selectedMonth]);
  useEffect(() => { if (step === 'person') fetchPersonData(); }, [selectedTeam, selectedMonth]);

  const handleNewPerson = async () => {
    try {
      const values = await newPersonForm.validateFields();
      await createSalesperson({ ...values, teamId: selectedTeam, status: 1 });
      message.success('新增成功');
      setNewPersonOpen(false);
      newPersonForm.resetFields();
      fetchPersonData();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    }
  };

  const handleTeamMonthSave = async () => {
    if (!yearRecord) { message.error('请先设定年度目标'); return; }
    const q = yearRecord.quarters?.find(q => q.quarter === selectedQuarter);
    if (!q) { message.error('季度数据不存在'); return; }

    const items = teamMonthData.map(tm => ({
      yearId: yearRecord.id,
      quarterId: q.id,
      teamId: tm.teamId || tm.id,
      month: selectedMonth,
      teamTargetAmount: tm.teamTargetAmount || 0,
    }));

    try {
      await batchSaveTaskTeamMonths(items);
      message.success('保存成功');
      fetchTeamMonths();
    } catch (err) { message.error(err.message); }
  };

  const handleBatchPerson = async () => {
    try {
      const values = await batchForm.validateFields();
      const tasks = personData.map(p => ({
        salespersonId: p.salespersonId || p.salesperson?.id,
        month: selectedMonth,
        targetAmount: values.targetAmount || 0,
        teamId: p.salesperson?.teamId || selectedTeam,
      })).filter(t => t.salespersonId);
      await batchCreateTasks(tasks);
      message.success('批量分配成功');
      setBatchOpen(false);
      fetchPersonData();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    }
  };

  const handlePersonAmountChange = (personId, value) => {
    setPersonData(prev => prev.map(p =>
      (p.salespersonId === personId || p.salesperson?.id === personId)
        ? { ...p, targetAmount: value }
        : p
    ));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        salespersonId: editRecord.salespersonId || editRecord.salesperson?.id,
        month: selectedMonth,
        targetAmount: values.targetAmount,
        teamId: editRecord.salesperson?.teamId || selectedTeam,
      };
      if (editRecord.id) {
        await updateSalesTask(editRecord.id, payload);
      } else {
        await createSalesTask(payload);
      }
      message.success('保存成功');
      setModalOpen(false);
      fetchPersonData();
    } catch (err) {
      if (err.errorFields) return; // validation error
      message.error(err.message);
    }
  };

  const handleBatchSavePerson = async () => {
    const tasks = personData
      .filter(p => (p.targetAmount || 0) > 0)
      .map(p => ({
        salespersonId: p.salespersonId || p.salesperson?.id,
        month: selectedMonth,
        targetAmount: p.targetAmount || 0,
        teamId: p.salesperson?.teamId || selectedTeam,
      }));
    if (!tasks.length) { message.warning('无有效任务数据'); return; }
    try {
      await batchCreateTasks(tasks);
      message.success('全部保存成功');
      fetchPersonData();
    } catch (err) { message.error(err.message); }
  };

  // Team total for the quarter
  const q = yearRecord?.quarters?.find(q => q.quarter === selectedQuarter);
  const quarterAmount = q ? Number(q.quarterAmount) : 0;
  const teamMonthTotal = teamMonthData.reduce((s, t) => s + Number(t.teamTargetAmount || 0), 0);

  // Person totals
  const personTotal = personData.reduce((s, p) => s + Number(p.targetAmount || 0), 0);
  const teamTarget = teamMonthData.find(t => (t.teamId === selectedTeam || t.id === selectedTeam))
    ?.teamTargetAmount || 0;

  const personColumns = [
    {
      title: '销售人员', dataIndex: ['salesperson', 'name'],
      render: (name, r) => name || r.salesperson?.name || '-',
    },
    {
      title: '月任务额', dataIndex: 'targetAmount',
      render: (v, r) => (
        <InputNumber
          value={Number(v || 0)}
          min={0}
          style={{ width: 160 }}
          formatter={(val) => `¥ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(val) => val.replace(/[¥,\s]/g, '')}
          onChange={(val) => handlePersonAmountChange(r.salespersonId || r.salesperson?.id, val || 0)}
        />
      ),
    },
    { title: '占比', render: (_, r) => {
      const pct = personTotal > 0 ? (Number(r.targetAmount || 0) / personTotal * 100) : 0;
      return `${pct.toFixed(1)}%`;
    }},
    { title: '已完成', dataIndex: 'completed', render: (v) => `¥${Number(v || 0).toLocaleString()}` },
    { title: '完成率', dataIndex: 'rate', render: (v) => {
      const val = Number(v || 0);
      return <Progress percent={Number(val.toFixed(1))} size="small" />;
    }},
    {
      title: '操作', render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => {
            setEditRecord(r);
            form.setFieldsValue({ targetAmount: r.targetAmount, salespersonId: r.salespersonId || r.salesperson?.id });
            setModalOpen(true);
          }}>编辑</Button>
          {r.id && (
            <Popconfirm title="确定删除？" onConfirm={async () => {
              try {
                await deleteSalesTask(r.id);
                message.success('已删除');
                fetchPersonData();
              } catch (err) { message.error(err.message); }
            }}>
              <Button size="small" danger>删除</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const teamColumns = [
    { title: '团队', dataIndex: ['team', 'name'], render: (name, r) => name || r.name || '-' },
    {
      title: '月任务额', dataIndex: 'teamTargetAmount',
      render: (v, r) => (
        <InputNumber
          value={Number(v || 0)}
          min={0}
          style={{ width: 180 }}
          formatter={(val) => `¥ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(val) => val.replace(/[¥,\s]/g, '')}
          onChange={(val) => {
            setTeamMonthData(prev => prev.map(t =>
              (t.teamId === r.teamId || t.id === r.teamId || (!t.teamId && t.id === r.id))
                ? { ...t, teamTargetAmount: val || 0 }
                : t
            ));
          }}
        />
      ),
    },
    {
      title: '团队任务分配情况', render: (_, r) => {
        const target = Number(r.teamTargetAmount || 0);
        const allocated = Number(r.allocatedAmount || 0);
        const pct = target > 0 ? (allocated / target * 100) : 0;
        return (
          <Space style={{ minWidth: 140 }}>
            <Progress percent={Number(pct.toFixed(1))} size="small" style={{ width: 100 }} format={(p) => `${p}%`} />
          </Space>
        );
      },
    },
    { title: '操作', render: (_, r) => (
      <Button type="link" icon={<UserOutlined />}
        onClick={() => {
          setSelectedTeam(r.teamId || r.id);
          setSelectedMonth(selectedMonth);
          setStep('person');
        }}
      >分配人员</Button>
    )},
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <span>任务分配</span>
            <Select value={selectedYear} onChange={(v) => { setSelectedYear(v); step === 'person' && setStep('team'); }} style={{ width: 100 }}>
              {years.map(y => <Select.Option key={y} value={y}>{y}年</Select.Option>)}
            </Select>
            <Select value={selectedQuarter} onChange={(v) => { setSelectedQuarter(v); step === 'person' && setStep('team'); }} style={{ width: 80 }}>
              {[1, 2, 3, 4].map(q => <Select.Option key={q} value={q}>Q{q}</Select.Option>)}
            </Select>
            <Select value={selectedMonth} onChange={(v) => { setSelectedMonth(v); setStep('team'); }} style={{ width: 120 }} options={monthOptions} />
          </Space>
        }
      >
        {step === 'team' ? (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Statistic title="季度目标" value={quarterAmount}
                  formatter={(v) => `¥${Number(v).toLocaleString()}`}
                />
              </Col>
              <Col span={6}>
                <Statistic title="本月已分配占比" value={quarterAmount > 0 ? (teamMonthTotal / quarterAmount * 100) : 0}
                  precision={1} suffix="%"
                  valueStyle={{ color: teamMonthTotal > quarterAmount ? '#ff4d4f' : '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic title="本月已分配金额" value={teamMonthTotal}
                  formatter={(v) => `¥${Number(v).toLocaleString()}`}
                />
              </Col>
              <Col span={6}>
                <Button type="primary" onClick={handleTeamMonthSave}
                  icon={<CheckCircleOutlined />} style={{ marginTop: 4 }}
                >保存团队分配</Button>
              </Col>
            </Row>

            <Table
              dataSource={teamMonthData}
              columns={teamColumns}
              rowKey={(r) => r.teamId || r.id}
              pagination={false}
              loading={loading}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><strong>合计</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <strong>¥{teamMonthTotal.toLocaleString()}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    {(() => {
                      const allocated = teamMonthData.reduce((s, t) => s + Number(t.allocatedAmount || 0), 0);
                      const pct = teamMonthTotal > 0 ? (allocated / teamMonthTotal * 100) : 0;
                      return (
                        <Space>
                          <Progress percent={Number(pct.toFixed(1))} size="small" style={{ width: 100 }} format={(p) => `${p}%`} />
                        
                        </Space>
                      );
                    })()}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                </Table.Summary.Row>
              )}
            />
          </>
        ) : (
          <>
            <Space style={{ marginBottom: 16 }}>
              <Button icon={<ArrowLeftOutlined />} onClick={() => { fetchTeamMonths(); setStep('team'); }}>返回团队分配</Button>
              <Tag color="blue">{teams.find(t => t.id === selectedTeam)?.name}</Tag>
              <span>{selectedMonth.replace('-01', '').replace('-', '年')}月</span>
              <Progress type="circle" percent={personTotal > 0 && teamTarget > 0 ? Number((personTotal / teamTarget * 100).toFixed(1)) : 0}
                size={30} format={(pct) => `${pct}%`}
              />
              <span>团队月目标: ¥{Number(teamTarget).toLocaleString()}</span>
              <span>已分配: ¥{personTotal.toLocaleString()}</span>
              {personTotal > teamTarget && <Tag color="red">超限!</Tag>}
              <Button type="primary" size="small" onClick={handleBatchSavePerson} icon={<CheckCircleOutlined />}>保存全部</Button>
              <Button size="small" onClick={() => setBatchOpen(true)}>批量设定</Button>
              <Button size="small" icon={<PlusOutlined />} onClick={() => { newPersonForm.resetFields(); setNewPersonOpen(true); }}>新增人员</Button>
            </Space>

            <Table
              dataSource={personData}
              columns={personColumns}
              rowKey={(r) => r.id || r.salespersonId || r.salesperson?.id}
              pagination={false}
              loading={loading}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><strong>合计</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <strong style={{ color: personTotal <= teamTarget ? '#52c41a' : '#ff4d4f' }}>
                      ¥{personTotal.toLocaleString()}
                      {personTotal > teamTarget ? ' ⚠️超限' : ''}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}><strong>100%</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              )}
            />

            <Modal title="编辑个人任务" open={modalOpen} onOk={handleSave}
              onCancel={() => setModalOpen(false)}
            >
              <Form form={form} layout="vertical">
                <Form.Item name="salespersonId" label="销售人员">
                  <Input disabled />
                </Form.Item>
                <Form.Item name="targetAmount" label="月任务额" rules={[{ required: true, message: '请输入' }]}>
                  <InputNumber min={0} style={{ width: '100%' }}
                    formatter={(val) => `¥ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(val) => val.replace(/[¥,\s]/g, '')}
                  />
                </Form.Item>
              </Form>
            </Modal>

            <Modal title="批量设定任务" open={batchOpen} onOk={handleBatchPerson}
              onCancel={() => setBatchOpen(false)}
            >
              <Form form={batchForm} layout="vertical">
                <Form.Item name="targetAmount" label="统一月任务额（覆盖所有人员）" rules={[{ required: true, message: '请输入' }]}>
                  <InputNumber min={0} style={{ width: '100%' }}
                    formatter={(val) => `¥ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(val) => val.replace(/[¥,\s]/g, '')}
                  />
                </Form.Item>
              </Form>
            </Modal>

            <Modal title="新增销售人员" open={newPersonOpen} onOk={handleNewPerson}
              onCancel={() => setNewPersonOpen(false)} destroyOnClose
            >
              <Form form={newPersonForm} layout="vertical">
                <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="phone" label="手机号" rules={[
                  { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' },
                ]}>
                  <Input maxLength={11} />
                </Form.Item>
              </Form>
            </Modal>
          </>
        )}
      </Card>
    </div>
  );
}
